/**
 * NewImportProjectModal Component
 * ----------------------------------------------------------
 * PURPOSE:
 * This modal is responsible for importing **Project Data**
 * into the CRM system through CSV/XLS/XLSX uploads.
 *
 * FEATURES:
 * ✔ Displays project import history (uploaded files, status, timestamps)
 * ✔ Allows uploading new project CSV files
 * ✔ Provides a generated CSV template based on configured headers
 *
 * PROPS:
 *  open            – Controls modal visibility
 *  close           – Callback to close modal
 *  organization_id – ID used for upload & file log fetching
 *  user            – Logged-in user (for uploader name)
 *  projectHeader   – Header configuration from Redux used to generate CSV template
 *  theme           – Light/dark mode flag
 *
 * SERVICES USED:
 *  fetchProjectFile()  → retrieves previous upload logs
 *  uploadprojectFile() → uploads new CSV/XLS/XLSX file
 */

import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import {fetchProjectFile,uploadprojectFile} from "../../../Services/contacts";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../theme";
import CSVGenerator from "./CSVGenerator";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";

type props = {
  theme: any;
  open: boolean;
  close: () => void;
  organization_id: string;
  history: any;
  usersList: any[];
  user: any;
  projectHeader: any;
};

const NewImportProjectModal: FunctionComponent<props> = ({
  theme,
  open,
  close,
  organization_id,
  history,
  usersList,
  user,
  projectHeader,
}) => {
  /** MUI theme config for responsiveness */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** Loader + upload history state */
  const [load, setLoad] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const dispatcher = useDispatch();

  /** CSV Template Types */
  interface DataObject {
    [key: string]: any;
  }

  /**
   * FILTERING PROJECT HEADERS FOR CSV TEMPLATE
   * ------------------------------------------------------------------
   * These determine what fields are included in the "Download Template" CSV.
   */
  const filtervalues = [
    "project_status",
    "project_name",
    "developer_name",
    "address",
    "rera_link",
    "walkthrough_link",
    "property_type",
    "property_stage",
    "status",
    "created_by",
  ];

  /** Keep only allowed fields */
  const reqData = projectHeader.filter((obj: any) =>
    filtervalues.includes(obj.label)
  );

  /** Additional computed header for CSV template */
  const dbData = [
    ...reqData,
    {
      label: "status",
      value: "Status",
    },
  ];

  /** Convert header objects to CSV header row */
  const headerData = dbData.map((item) => item.value);
  const projectHeaderData: DataObject[] = [headerData];

  /**
   * LOAD PREVIOUS PROJECT IMPORT LOGS
   * ------------------------------------------------------------------
   * Fetch import history upon modal mount and refresh when org ID changes.
   */
  useEffect(() => {
    if (organization_id) {
      const unsub = fetchProjectFile(
        organization_id,
        (data: any) => setUploadedFiles(data),
        dispatcher
      );
      return () => unsub();
    }
  }, [organization_id]);

  /**
   * UPLOAD PROJECT FILE
   * ------------------------------------------------------------------
   * Passes file to backend processing service.
   */
  const upload = async (file: any) => {
    setLoad(true);

    uploadprojectFile(
      file,
      organization_id,
      dispatcher,
      (data: boolean) => setLoad(data),
      `${user.user_first_name} ${user.user_last_name}`
    );
  };

  /** Row styling based on theme */
  const tdStyles = {
    backgroundColor: theme
      ? themeColors.tdBackgroundColor
      : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

    const thStyles = {
        backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
    }

    const modalStyle: Styles = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        content: {
            background: theme
                ? themeColors.cardBackgroundColor
                : themeColors.cardBackgroundColorDark,
            color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
            borderRadius: 16,
            padding: isMobile ? "24px 12px" : "10px 20px",
            border: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            inset: "auto",
            margin: "auto",
        },
    };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <div className={styles.child}>
          {/* Modal Title */}
          <h5
            style={{
              fontFamily: "var(--font-family-main)",
              fontSize: "var(--font-size-large)",
              paddingTop: isMobile ? "unset" : "15px",
            }}
          >
            Import Data for "Project Form"
          </h5>

          {/* TABLE: IMPORT LOGS */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead
                className={styles.head}
                style={{
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-small)",
                }}
              >
                <tr>
                  <th className={styles.th} style={thStyles}>Sno.</th>
                  <th className={styles.th} style={thStyles}>Request Id</th>
                  <th className={styles.th} style={thStyles}>Created At</th>
                  <th className={styles.th} style={thStyles}>Status</th>
                  <th className={styles.th} style={thStyles}>Upload Count</th>
                  <th className={styles.th} style={thStyles}>Uploaded File</th>
                  <th className={styles.th} style={thStyles}>Processed File</th>
                </tr>
              </thead>

              <tbody>
                {uploadedFiles.map((item: any, index: number) => (
                  <tr key={index} className={styles.no}>
                    <td className={styles.td} style={tdStyles}>{index + 1}</td>

                    <td className={styles.td} style={tdStyles}>{item.request_id}</td>

                    <td className={styles.td} style={tdStyles}>
                      {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                    </td>

                    <td className={styles.td} style={tdStyles}>{item.status}</td>

                    <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>

                    <td className={styles.td} style={tdStyles}>
                      <a href={item.fileUrl}>Uploaded File</a>
                    </td>

                    <td className={styles.td} style={tdStyles}>
                      {item.responseURL !== "" && (
                        <a href={item.responseURL}>Processed File</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTION BUTTONS */}
          <div className={styles.selectBox}>
            <div style={{ marginRight: 8 }}>
              <StyledCancelButton onClick={close}>Cancel</StyledCancelButton>
            </div>

            {/* DOWNLOAD CSV TEMPLATE */}
            <div style={{ marginRight: 8 }}>
              <CSVGenerator data={projectHeaderData} />
            </div>

            {/* UPLOAD FILE BUTTON */}
            <div>
              <input
                id="file-input"
                className={styles.select}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e: any) => upload(e.target.files[0])}
              />

              <StyledSubmitButton
                onClick={() => document.getElementById("file-input")?.click()}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} /> Upload...
                  </span>
                ) : (
                  <>Upload File</>
                )}
              </StyledSubmitButton>
            </div>
          </div>

          {/* FOOTER TIPS */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <p
              style={{
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-small)",
              }}
            >
              <i className="fas fa-info-circle"></i> Only .csv file type is supported.
            </p>

            <p
              style={{
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-small)",
                marginTop: "-1rem",
              }}
            >
              <i className="fas fa-info-circle"></i> Please ensure all mandatory fields
              are filled.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  theme: state.theme.isLightMode,
  projectHeader: state.projectHeaderList.data,
});

export default connect(mapStateToProps)(NewImportProjectModal);
